import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { translate } from '~l10n/client';
import Backbone from 'backbone';

import { toast } from '~shared/components/Toast';
import Profile from '~teacher/components/Profile/Profile';
import { RouterProvider } from '~teacher/context/RouterContext';
import { queryClient } from '~teacher/queryClient';

import Constants from '../../../../../shared/Constants';
import teacher from '../../../../../shared/models/TeacherModel';
import user from '../../../../../shared/models/user';
import { ErrorView } from '../../../../app/shared/error/ErrorView';
import header from '../../../../app/teacher/header/HeaderController';
import dots from '../../../../shared/components/buttons/Dots';

let router = null;
const model = teacher.account;
const licenseModel = model.licenseModel;

class ProfileController {
  doProfile(teacherRouter) {
    router = teacherRouter;
    header.render();

    if (router.isEnteringProfile()) {
      model.resetPasswordFields();
      model.restoreInitialValues();
      model.banner.message = '';
    }

    if (!model.initialEmail) {
      licenseModel.buyerId = user.getId();
      licenseModel.seats = 1;
      licenseModel.years = 1;
      licenseModel.licenseStep = Constants.LICENSE_STEP;
      model.buyingLicense = false;
      model.fetchAccount(() =>
        licenseModel.fetchPlan(() => {
          controller.renderView();
        })
      );
    } else {
      controller.renderView();
    }
  }

  renderView() {
    if (Backbone.history.fragment === 'profile/info') {
      ReactDOM.render(
        <ErrorView>
          <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools initialIsOpen={false} />
            <RouterProvider router={router}>
              <Profile
                controller={controller}
                model={model}
                selectedTab={Constants.PROFILE_INFO}
                router={router}
                passwordFieldsVisible={
                  !model.showChangePassword &&
                  !(model.mcUser || model.googleUser)
                }
              />
            </RouterProvider>
          </QueryClientProvider>
        </ErrorView>,
        document.getElementById('main-content')
      );
    }
  }

  firstNameChanged(firstName) {
    model.firstName = firstName;
    model.errors.firstName.error = false;
    controller.renderView();
  }

  lastNameChanged(lastName) {
    model.lastName = lastName;
    model.errors.lastName.error = false;
    controller.renderView();
  }

  phoneNumberChanged({ value }) {
    model.phoneNumber = value ? value.trim() : null;
    controller.renderView();
  }

  changePasswordClicked() {
    model.showChangePassword = false;
    controller.renderView();
  }

  currentPasswordChanged(currentPassword) {
    model.currentPassword = currentPassword;
    model.errors.currentPassword.error = false;
    controller.renderView();
  }

  newPasswordChanged(newPassword) {
    model.password = newPassword;
    model.errors.password.error = false;
    controller.renderView();
  }

  newPassword2Changed(newPassword2) {
    model.password2 = newPassword2;
    model.errors.password2.error = false;
    controller.renderView();
  }

  showPasswordsChanged() {
    model.showPasswords = !model.showPasswords;
    controller.renderView();
  }

  saveClicked() {
    dots.start('profile-save-button');
    model.validateProfile(controller.saveValidationResult);
  }

  saveValidationResult() {
    if (
      model.errors.firstName.error ||
      model.errors.lastName.error ||
      model.errors.currentPassword.error ||
      model.errors.password.error ||
      model.errors.password2.error
    ) {
      dots.stop();
      controller.renderView();
    } else {
      if (!model.validateProfileDemographics()) {
        dots.stop();
        router.routeToProfileDemo();
      } else {
        model.saveAccount(controller.accountSaved);
      }
    }
  }

  accountSaved(result) {
    if (result.success) {
      user.setFirstName(model.firstName); // Update the user model for the header (SOC-1036).
      licenseModel.setPlanId(model.orgType);

      if (model.language !== user.getLanguage()) {
        user.setLanguage(model.language);
        router.routeToTeacherLogin();
      } else {
        dots.stop();
        toast.positive(translate('Profile updated!'));
        header.render();
        controller.renderView();
      }
    } else {
      if (result.oldPasswordWrong) {
        dots.stop();
        controller.renderView();
      } else if (result.authFailure) {
        router.routeToTeacherLogin();
      }
    }
  }
}

const controller = new ProfileController();
module.exports = controller;
